import { useIsMutating, useMutation } from '@tanstack/react-query'
import { exportLoans, exportCreditLimits, exportRepayments, exportRedirections } from '../services/exportServices'
import { QueryStringInput } from '@hello-aria/query-string'
import { useMe } from '@/shared/hooks/useMe'
import { useCallback, useRef } from 'react'

export type ExportVars = { query?: QueryStringInput; signal?: AbortSignal } | undefined

type LoansExportData = Awaited<ReturnType<typeof exportLoans>>
type CreditLimitsExportData = Awaited<ReturnType<typeof exportCreditLimits>>
type RepaymentsExportData = Awaited<ReturnType<typeof exportRepayments>>
type RedirectionsExportData = Awaited<ReturnType<typeof exportRedirections>>

type ExportReturnMap = {
    loans: LoansExportData
    creditLimits: CreditLimitsExportData
    repayments: RepaymentsExportData
    redirections: RedirectionsExportData
}

export type ExportKey = keyof ExportReturnMap

const exportQueryKeys = {
    all: ['export'] as const,
    byType: (type: ExportKey) => [...exportQueryKeys.all, type] as const
} as const

const EXPORT_CONFIG = {
    loans: exportLoans,
    creditLimits: exportCreditLimits,
    repayments: exportRepayments,
    redirections: exportRedirections
} as const

type ExportServiceFn<T extends ExportKey> = (companyId: string, variables?: ExportVars) => Promise<ExportReturnMap[T]>

export function useExport<T extends ExportKey>(type: T) {
    const { companyId } = useMe()

    const mutationKey = exportQueryKeys.byType(type)
    const isMutating = useIsMutating({ mutationKey }) > 0
    const serviceFn = EXPORT_CONFIG[type] as ExportServiceFn<T>

    const controllerRef = useRef<AbortController | null>(null)

    const mutation = useMutation<ExportReturnMap[T], unknown, ExportVars>({
        mutationKey,
        mutationFn: async (variables) => {
            controllerRef.current?.abort()
            const controller = new AbortController()
            controllerRef.current = controller

            try {
                return await serviceFn(companyId, { ...variables, signal: controller.signal })
            } finally {
                if (controllerRef.current === controller) controllerRef.current = null
            }
        }
    })

    const cancel = useCallback(() => {
        controllerRef.current?.abort()
    }, [])

    return { ...mutation, isMutating, cancel }
}
